import { useState } from 'react';

import {
  useCreateHistoricalScenarioMutation,
  useUpdateHistoricalScenarioMutation,
  useDeleteHistoricalScenarioMutation,
  useGetAvailableScenariosQuery,
} from '@/redux/api';

// Types
import HistoricalScenario from '@/types/historical-scenario';
import Nullable from '@/types/nullable';
import { UUID } from '@/types/global';

// Utils
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';

export const useHistoricalScenariosModals = ({ resetFilter }: { resetFilter: () => void }) => {
  const [createScenario, { isLoading: isLoadingCreate }] = useCreateHistoricalScenarioMutation();
  const [updateScenario, { isLoading: isLoadingUpdate }] = useUpdateHistoricalScenarioMutation();
  const [deleteScenario, { isLoading: isLoadingDelete }] = useDeleteHistoricalScenarioMutation();

  const { data: scenariosData } = useGetAvailableScenariosQuery();
  const customScenarios = scenariosData?.customScenarios || [];

  const [customScenarioToUpdate, setCustomScenarioToUpdate] = useState<Nullable<HistoricalScenario>>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const handleOnCreateScenario = () => {
    setShowCreateModal(true);
    resetFilter();
  };

  const handleOnDeleteScenario = (id: UUID) => {
    const selectedScenario = customScenarios?.find((cS: HistoricalScenario) => cS._id === id) ?? null;
    if (selectedScenario) {
      setCustomScenarioToUpdate(selectedScenario);
      setShowDeleteModal(true);
      resetFilter();
    }
  };

  const handleOnEditScenario = (id: UUID) => {
    const selectedScenario = customScenarios?.find((cS: HistoricalScenario) => cS._id === id) ?? null;
    if (selectedScenario) {
      setCustomScenarioToUpdate(selectedScenario);
      setShowEditModal(true);
      resetFilter();
    }
  };

  const onCloseModal = () => {
    if (showCreateModal) setShowCreateModal(false);
    if (showDeleteModal) setShowDeleteModal(false);
    if (showEditModal) setShowEditModal(false);
    setCustomScenarioToUpdate(null);
  };

  const onConfirmDeleteScenario = async (id: UUID) => {
    if (!id) return setShowDeleteModal(false);
    try {
      await deleteScenario(id).unwrap();
      notification.success(formatMessage('Comparison.HistoricalScenarios.DeleteModal.DeletedNotificationBody'));
    } catch (error: any) {
      if (error.status !== 404) {
        notification.error(formatMessage('Comparison.HistoricalScenarios.DeleteModal.DeleteErrorNotificationBody'));
      }
    } finally {
      setShowDeleteModal(false);
    }
  };

  const onSubmitCreateScenario = async (scenario: Partial<HistoricalScenario>) => {
    if (!scenario) return setShowCreateModal(false);
    try {
      await createScenario(scenario).unwrap();
      notification.success(formatMessage('Comparison.HistoricalScenarios.CreateModal.CreatedNotificationBody'));
    } catch (error: any) {
      if (error.status !== 404) {
        notification.error(formatMessage('Comparison.HistoricalScenarios.CreateModal.CreateErrorNotificationBody'));
      }
    } finally {
      setShowCreateModal(false);
    }
  };

  const onSubmitEditScenario = async (scenario: Partial<HistoricalScenario>) => {
    if (!scenario || !scenario?._id) return setShowEditModal(false);
    try {
      await updateScenario({ scenarioId: scenario._id, scenario }).unwrap();
      // Redux state update now handled automatically in the mutation's onQueryStarted
      notification.success(formatMessage('Comparison.HistoricalScenarios.EditModal.EditedNotificationBody'));
    } catch (error: any) {
      if (error.status !== 404) {
        notification.error(formatMessage('Comparison.HistoricalScenarios.EditModal.EditErrorNotificationBody'));
      }
    } finally {
      setShowEditModal(false);
    }
  };

  return {
    customScenarioToUpdate,
    handleOnCreateScenario,
    handleOnDeleteScenario,
    handleOnEditScenario,
    isLoadingCreate,
    isLoadingDelete,
    isLoadingUpdate,
    onCloseModal,
    onConfirmDeleteScenario,
    onSubmitCreateScenario,
    onSubmitEditScenario,
    showCreateModal,
    showDeleteModal,
    showEditModal,
  };
};
